import { Link } from 'react-router-dom';
import { LayoutGrid, Minimize2 } from 'lucide-react';
import { useChrome } from '../context/ChromeContext';
import { WORKTOP_PATH } from '@/lib/arche-url';
import { useGlobalFiasAIOptional } from '@/components/fias-ai/hooks/GlobalFiasAIContext';

interface ExpandedArcheHeaderProps {
  /** Canonical arche id (`arche_*`) whose focus-mode chrome this bar restores. */
  archeId: string;
}

const LINK =
  'flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-primary';

/** The four focus-mode controls, each on its own so a bar can group them. */
function FiasHomeLink() {
  return (
    <Link
      to="/"
      className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm text-muted-foreground transition-colors hover:text-primary"
      title="Back to Fias"
      aria-label="Back to Fias"
    >
      <img src="/fias-logo.png" alt="" className="h-5 w-auto dark:invert" />
    </Link>
  );
}

function WorktopLink({ labelClassName }: { labelClassName: string }) {
  return (
    <Link to={WORKTOP_PATH} className={LINK} title="Go to Worktop" aria-label="Go to Worktop">
      <LayoutGrid className="h-4 w-4" />
      <span className={labelClassName}>Worktop</span>
    </Link>
  );
}

function FiasAIButton({ labelClassName }: { labelClassName: string }) {
  // Optional: only when the global assistant provider is mounted (it is
  // app-wide, but arche unit tests render these controls without it).
  const ai = useGlobalFiasAIOptional();
  if (!ai) return null;
  return (
    <button
      type="button"
      onClick={() => ai.openPanel()}
      className={LINK}
      title="Open Fias AI"
      aria-label="Open Fias AI"
    >
      <img src="/Heptagen_Logo_1.png" alt="" className="h-5 w-5" />
      <span className={labelClassName}>Fias AI</span>
    </button>
  );
}

function CollapseButton({ archeId, labelClassName }: { archeId: string; labelClassName: string }) {
  const { showHeaders } = useChrome();
  return (
    <button
      type="button"
      onClick={() => showHeaders(archeId)}
      className={LINK}
      title="Collapse to standard view"
      aria-label="Collapse to standard view"
    >
      <Minimize2 className="h-4 w-4" />
      <span className={labelClassName}>Collapse</span>
    </button>
  );
}

/**
 * The focus-mode controls in one inline group — Fias home, Worktop, Fias AI,
 * Collapse — for a surface that puts them in its OWN toolbar instead of the
 * thin bar below (the Vault workspace puts them beside its view tabs). Renders
 * nothing unless this arche is expanded, like the bar.
 */
export function ExpandedArcheControls({
  archeId,
  labelClassName = 'hidden sm:inline',
  className = '',
}: {
  archeId: string;
  /** When the text labels show (they're `aria-label`led either way). */
  labelClassName?: string;
  className?: string;
}) {
  const { isHeadersHidden } = useChrome();
  if (!isHeadersHidden(archeId)) return null;
  return (
    <div className={`flex items-center gap-0.5 ${className}`}>
      <FiasHomeLink />
      <WorktopLink labelClassName={labelClassName} />
      <FiasAIButton labelClassName={labelClassName} />
      <CollapseButton archeId={archeId} labelClassName={labelClassName} />
    </div>
  );
}

/**
 * The thin top bar shown while an arche is EXPANDED (focus mode — the platform
 * Header, the suggested-arches sidebar, and the arche's own ArcheHeader are all
 * hidden so the arche fills the viewport). Once chrome is hidden it is the ONLY
 * in-app way back, so every focus-mode-capable arche must surface it:
 *
 * - Normal-mode arches get it for free — `ArcheLayout` renders it centrally.
 * - An arche with its OWN layout (e.g. email) passes `hideExpandedHeader` to
 *   `ArcheLayout` and renders its own restore affordance (`FocusModeRestoreControls`,
 *   or these same controls via `ExpandedArcheControls`) in a spot that fits its
 *   chrome instead.
 *
 * Four controls: leave to Fias home, jump to the Worktop, open Fias AI, and
 * collapse back to the standard (chrome-shown) view. Home and Worktop are
 * separate destinations — `/` is the marketing/landing home, the Worktop is the
 * user's own arche board — and in focus mode the platform Header that normally
 * carries the Worktop link is hidden, so this bar is the only route to it.
 * Self-guards on this arche's chrome-hidden state,
 * so it is inert (renders nothing) when the arche is not expanded — including in
 * arche tests that render it without a ChromeProvider (the context's safe
 * default reports "not hidden").
 */
export function ExpandedArcheHeader({ archeId }: ExpandedArcheHeaderProps) {
  const { isHeadersHidden } = useChrome();
  if (!isHeadersHidden(archeId)) return null;

  const labelClassName = 'hidden sm:inline';
  return (
    <div className="flex-shrink-0 border-b border-border bg-card/80 backdrop-blur-sm">
      <div className="flex items-center justify-between gap-2 px-3 py-1.5">
        <div className="flex items-center gap-1">
          <FiasHomeLink />
          <WorktopLink labelClassName={labelClassName} />
        </div>

        <div className="flex items-center gap-1">
          <FiasAIButton labelClassName={labelClassName} />
          <CollapseButton archeId={archeId} labelClassName={labelClassName} />
        </div>
      </div>
    </div>
  );
}

export default ExpandedArcheHeader;
